import React from "react";
import { IconButton, Tooltip, CircularProgress } from "@mui/material";
import { LockOutlined, Star, StarBorder } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useFollowPlayer, useUnfollowPlayer, scoutingKeys } from "../../hooks/queries/useScouting";
import colors from "../../design-system/colors";
import { useAuth } from "../../context/AuthContext";
import { useLicenseUpgrade } from "../license/LicenseUpgradeDialog";

/**
 * FollowPlayerButton — Star toggle that follows/unfollows a player.
 *
 * Props:
 *  - jugadorId {number|string} required
 *  - jugadorNombre {string}
 *  - equipoNombre {string}
 *  - equipoId {number}
 *  - size {"small"|"medium"|"large"}  default "small"
 */
const FollowPlayerButton = ({
  jugadorId,
  jugadorNombre = "",
  equipoNombre = "",
  equipoId = null,
  size = "small",
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, hasPermission } = useAuth();
  const { requestUpgrade } = useLicenseUpgrade();
  const canManageScouting = hasPermission("scouting.manage");

  const legacyUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("usuario") || "null");
    } catch {
      return null;
    }
  })();
  const userId = authUser?.id || legacyUser?.oid || legacyUser?.sub || legacyUser?.id || null;

  const followedPlayers =
    queryClient.getQueryData(scoutingKeys.players(userId)) || [];
  const isFollowed = followedPlayers.some(
    (p) => String(p.jugadorId) === String(jugadorId)
  );

  const { mutate: follow, isPending: following } = useFollowPlayer();
  const { mutate: unfollow, isPending: unfollowing } = useUnfollowPlayer();

  const isPending = following || unfollowing;

  if (!userId) return null;

  if (!canManageScouting) {
    return (
      <Tooltip title="Disponible con una licencia de scouting" placement="top">
        <IconButton
          size={size}
          aria-label="Desbloquear seguimiento de jugadores"
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            if (requestUpgrade) {
              requestUpgrade("scouting.manage");
              return;
            }
            const returnTo = `${location.pathname}${location.search}`;
            navigate(
              `/licencias?required=scouting.view&returnTo=${encodeURIComponent(returnTo)}`
            );
          }}
          sx={{ color: colors.neutral[500] }}
        >
          <LockOutlined fontSize={size} />
        </IconButton>
      </Tooltip>
    );
  }

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (isPending) return;

    if (isFollowed) {
      unfollow({ userId, jugadorId });
    } else {
      follow({
        userId,
        jugadorId,
        playerData: { jugadorNombre, equipoNombre, equipoId },
      });
    }
  };

  return (
    <Tooltip
      title={isFollowed ? "Dejar de seguir" : "Seguir jugador"}
      placement="top"
    >
      <span>
        <IconButton
          size={size}
          onClick={handleClick}
          disabled={isPending}
          aria-label={isFollowed ? "Dejar de seguir" : "Seguir jugador"}
          sx={{
            color: isFollowed ? colors.secondary[600] : colors.neutral[400],
            transition: "color 0.2s",
            "&:hover": {
              color: isFollowed ? colors.secondary[800] : colors.secondary[500],
              backgroundColor: "rgba(255, 193, 7, 0.08)",
            },
          }}
        >
          {isPending ? (
            <CircularProgress size={size === "small" ? 16 : 20} color="inherit" />
          ) : isFollowed ? (
            <Star fontSize={size} />
          ) : (
            <StarBorder fontSize={size} />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default FollowPlayerButton;
